import moment from 'moment';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import { isValid } from './device-monitoring-chart-layer-cell';
import '../../common/bn-property';
import { PAYLOAD_TYPE } from '../props/device-event-payload-type';

const SECONDS_PER_READING = 4 * 60 * 60;
const DATE_FORMAT_STRING = 'MMM D YYYY, h:mm a';

class DeviceMonitoringChartHeaderDetails extends LitEntity {
  constructor() {
    super();
    this.visibleLayers = [];
  }

  static get properties() {
    return {
      dateHovering: { type: String, attribute: 'date-hovering' },
      noReportSeconds: { type: Number, attribute: 'no-report-seconds' },
      visibleLayers: { type: Array, attribute: 'visible-layers' },
      levels: { type: Array, attribute: false },
      events: { type: Array, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .header-details {
        text-align: center;
        flex-direction: column;
      }
      .header-title {
        display: flex;
        flex-direction: row;
        justify-content: center;
        flex-wrap: wrap;
      }
      bn-property {
        margin: 0 .5rem;
        min-width: 4rem;
      }
      bn-property[label="Date"] {
        min-width: 8rem;
      }
      .error {
        font-size: 0.8rem;
        font-weight: bold;
      }
      .area-text {
        align-items: center;
      }
      .spacer {
        opacity: 0;
        user-select: none;
        pointer-events: none;
      }
    `];
  }

  render() {
    const titleHtml = this._getTitleSub();
    if (!titleHtml) {
      return html``;
    }

    return html`
      <div class="row header-details">
        <div class="header-title">
          ${titleHtml}
        </div>
      </div>
    `;
  }

  _getTitleSub() {
    if (!isNaN(this.noReportSeconds)) {
      return [
        html`<bn-property stacked class="area-text" label="No Readings for">${moment().subtract(this.noReportSeconds, 'seconds').fromNow(true)}</bn-property>`,
        html`<bn-property stacked class="area-text" label="Readings Missed">${Math.round(this.noReportSeconds / SECONDS_PER_READING)}</bn-property>`,
      ];
    }

    if (!this.dateHovering) {
      return this._renderSpacer();
    }

    const level = (this.levels || []).find((level) => level.rawDate === this.dateHovering);
    const event = (this.events || []).find((event) => event.rawDate === this.dateHovering);
    if (!level && !event) {
      return this._renderSpacer();
    }

    return html`
      ${renderIf(this.visibleLayers.indexOf('levels') > -1, () => this._renderValue({ value: level && level.value, unit: 'm³', label: 'Volume' }))}
      ${renderIf(this.visibleLayers.indexOf('battery') > -1, () => this._renderValue({ value: event && event.batVolt, unit: 'V', label: 'Battery' }))}
      ${renderIf(this.visibleLayers.indexOf('batteryTemperature') > -1, () => this._renderValue({ value: event && event.batteryTemperature, unit: '°C', label: 'Battery Temperature' }))}
      ${renderIf(this.visibleLayers.indexOf('sensorTemperature') > -1, () => this._renderValue({ value: event && event.sensorTemperature, unit: '°C', label: 'Sensor Temperature' }))}
      ${renderIf(this.visibleLayers.indexOf('cell') > -1, () => this._renderValue({ value: isValid(event) ? event.cellStrength : undefined, unit: 'db', label: 'Cell' }))}
      <bn-property stacked label="Firmware Version">${this._formatVersion(event.version)}</bn-property>
      <bn-property stacked label="Date">${moment(event && event.date).format(DATE_FORMAT_STRING)}</bn-property>
      ${renderIf(event && event.sensorCriticalError, () => html`
        <bn-property stacked label="Sensor">
          <span class="error">${event.sensorCriticalError.title}</span>
        </bn-property>
      `)}
      ${renderIf(event && event.lastRunError, () => html`
        <bn-property stacked label="Last Run Error">
          <span class="error">${event.lastRunError.title}</span>
        </bn-property>
      `)}
      ${renderIf(event && event.payloadType !== PAYLOAD_TYPE.RUN && event.payloadType !== undefined, () => html`
        <bn-property stacked label="Event Type">
          <device-event-payload-type payload-type="${event.payloadType}"></device-event-payload-type>
        </bn-property>
      `)}
    `;
  }

  _renderValue({ unit, label, value }) {
    let valueRender = html`None`;
    if (value !== undefined) {
      valueRender = html`${value}<span class="unit">${unit}</span>`;
    }

    return html`<bn-property stacked label="${label}">${valueRender}</bn-property>`;
  }

  _renderSpacer() {
    return html`<bn-property stacked label="-" class="spacer">-</bn-property>`;
  }

  _formatVersion(version) {
    return `${version?.major}.${version?.minor}.${version?.build}`;
  }
}

window.customElements.define('device-monitoring-chart-header-details', DeviceMonitoringChartHeaderDetails);
